import { EventEmitter } from "eventemitter3";

import {
    ProcessedCertificateReference,
    CertificateListPreProcessingArguments, CertificateListPreProcessingResult,
    SignatureProcessStartArguments, StartSessionStateResult, Statistics,
    BatchProcessingArguments, SessionStateResult, BatchProcessingOutput,
    SignerSessionHttpServices, SignatureInput, ErrorInfo
} from "./http-models";

import { CoreResponseModel, CertificateInfo } from "./common";

import { AbstractJavaSession, SignerCore } from "./core";

/** Enumeração de estado da sessão de assinatura
 */
export enum SignerSessionState
{
    NotStarted = 0,
    CreatingCoreSession = 1,
    CoreSessionCreated = 2,
    StartingSignature = 3,
    SignatureStarted = 4,
    ReportingError = 5,
    Finishing = 6,
    Finished = 7
}

/** Enumeração de estado de cache de certificados
 */
export enum CertificateCacheState
{
    Initial = 0,
    LoadingCertificates = 1,
    CertificatesLoaded = 2,
    ProcessingCertificates = 3,
    CertificatesProcessed = 4
}

/** Classe que representa um cache de certificado
 */
export class CertificateCache
{
    private cachedResponse: CoreResponseModel;

    private events: EventEmitter = new EventEmitter();

    private state: CertificateCacheState = CertificateCacheState.Initial;

    private certListProcessingScheduled: boolean = false;

    private processed: boolean = false;

    private coreSessionResponseHandler: (CoreReponseModel, coreSession: AbstractJavaSession) => void;

    private processLoadedCertListHandler: (CoreReponseModel) => void;
    
    /** Construtor
     * @param certProcessingEnabled  Define se o pré-processamento de certificados deve estar habilitado
     * @param http  Objeto responsável por comunicação com webservice de assinatura digital
     */
    constructor(
        private certProcessingEnabled: boolean,
        private http: SignerSessionHttpServices
    )
    {
        this.coreSessionResponseHandler = this.handleCoreSessionResponse.bind(this);
        this.processLoadedCertListHandler = this.processCertList.bind(this);
    }

    /** Adiciona handler para evento de lista de certificados processada
     */
    add_certListReady(handler: (CoreResponseModel) => void): void { this.events.on("certListReady", handler); }
    /** Remove handler para evento de lista de certificados processada
     */
    remove_certListReady(handler: (CoreResponseModel) => void): void { this.events.removeListener("certListReady", handler); }

    //sub: _fireCertListReadyEvent
    private fireCertListReadyEvent(response: CoreResponseModel) : void {
        this.events.emit("certListReady", response);
        this.events.removeAllListeners("certListReady");
    }

    /** Adiciona handler para evento de lista de certificados carregada e processada, quando necessário
     * @param handler  Processo a ser executado
     */
    add_certListLoaded(handler: (CoreResponseModel) => void) { this.events.on("certListLoaded", handler); }
    /** Remove handler para evento de lista de certificados carregada e processada, quando necessário
     * @param handler  Processo a ser executado
     */
    remove_certListLoaded(handler: (CoreResponseModel) => void) { this.events.removeListener("certListLoaded", handler); }

    //sub: _fireCertListLoadedEvent
    private fireCertListLoadedEvent(response: CoreResponseModel) : void {
        this.events.emit("certListLoaded", response);
        this.events.removeAllListeners("certListLoaded");
    }

    /** Verifica se lista de certificados está carregada
     * @returns Verdadeiro se lista de certificados está carregada, senão falso
     */
    is_loaded() : boolean { return this.state >= CertificateCacheState.CertificatesLoaded; }

    /** Verifica se lista de certificados já foi processada
     * @returns Verdadeiro se lista de certificados já foi processada, senão falso
     */
    is_processed() : boolean { return this.state >= CertificateCacheState.CertificatesProcessed; }

    /** Verifica se o pré-processamento da lista de certificados está habilitada
     * @returns Verdadeiro se o pré-processamento da lista de certificados está habilitada, senão falso
     */
    is_certProcessingEnabled() : boolean { return this.certProcessingEnabled; }

    /** Verifica se lista de certificados já está carregada e processada se necessário
     * @returns Verdadeiro se lista de certificados já está pronta para ser utilizada, senão falso
     */
    is_certListReady() : boolean {
        return this.is_certProcessingEnabled() ? this.is_processed() : this.is_loaded();
    }

    //sub: _filterCertificateList
    private filterCertificateList(allowedCertificates: ProcessedCertificateReference[]) : void {
        //converte o array de certificados permitidos em uma hashtable
        const filtered: { [certId: string] : ProcessedCertificateReference } = {};
        if (allowedCertificates && allowedCertificates.length > 0) {
            //percorre os certificados filtrados adicionando no mapa
            for (let i = allowedCertificates.length - 1; i >= 0; --i) {
                const certObj: ProcessedCertificateReference = allowedCertificates[i];
                filtered[certObj.certId] = certObj;
            }
        }

        //pega resultado da resposta original
        const responseResult: { certs: CertificateInfo[] } = this.cachedResponse.result;

        //filtra array de certificados utilizado originalmente
        const newList: CertificateInfo[] = [];
        for (let i = 0, _i = responseResult.certs.length; i < _i; ++i) {
            //pega certificado original e verifica se ele está mapeado na hashtable de filtrados
            const originalCert: CertificateInfo = responseResult.certs[i];
            const allowed: ProcessedCertificateReference = filtered[originalCert.certID];
            if (allowed) {
                //caso o servidor trouxe UserData associado à referência do certificado, copia isso para o objeto original
                if (allowed.userData) originalCert.userData = allowed.userData;
                //adiciona certificado permitido na nova lista de certificados
                newList.push(originalCert);
            }
        }

        //joga lista de certificados filtrados no resultado da resposta original
        responseResult.certs = newList;
    }

    private handleServerCallback(rawResult: CertificateListPreProcessingResult, rawError: any) : void {
        //se veio resposta do callback para servidor quando não estamos em um estado que espera isso, vamos ignorar
        if (this.state !== CertificateCacheState.ProcessingCertificates)
            return console.error("Recebida resposta de callback de processamento de lista de certificados, porém ela foi DESCARTADA pois o estado atual do cache de certificados está com valor " + this.state);

        //mudamos estado para "processado"
        this.state = CertificateCacheState.CertificatesProcessed;

        //vamos inspecionar se recebemos um erro de request, jogando na resposta original
        if (rawError) {
            this.cachedResponse.error = { msg: rawError.toString(), stacktrace: null, type: null };
        }
        else {
            //se o resultado apresenta erro, registra isso na resposta. Caso contrário aplica filtro retornado pelo callback.
            if (rawResult.error) this.cachedResponse.error = rawResult.error;
            else this.filterCertificateList(rawResult.certs);
        }

        //invoca evento de finalização
        this.fireCertListReadyEvent(this.cachedResponse);
    }

    //sub: _processCertList
    private processCertList(response: CoreResponseModel) : void {
        //variável que indica quando não temos certificado algum para processar em server-side
        const noCerts = !response || response.error || !response.result || !response.result.certs || response.result.certs.length < 1;

        //se o conjunto de certificados é vazio, vamos marcar que "processamos" a lista e disparamos eventos de 'Ready'
        if (noCerts) {
            this.state = CertificateCacheState.CertificatesProcessed;
            return this.fireCertListReadyEvent(response);
        }

        //avançamos o estado para "processando certificados"
        this.state = CertificateCacheState.ProcessingCertificates;

        //cria argumentos para o callback de processamento, com os certificados listados e muda estado deste componente para "processando"
        const serverCallbackArgs: CertificateListPreProcessingArguments = { certs: [] };

        //converte os certificados recebidos em argumentos para o callback de processamento
        const recvCerts: CertificateInfo[] = response.result.certs;
        for (let i = 0, _i = recvCerts.length; i < _i; ++i) {
            const recvCert = recvCerts[i];
            //adiciona na lista de referências de certificados a serem enviados para o server-side
            serverCallbackArgs.certs.push({
                certId: recvCert.certID,
                certChain: recvCert.certChain
            });
        }

        //com nossa lista de certificados em mãos, vamos fazer um callback para o servidor
        this.http.processCertificates(serverCallbackArgs)
            .then(res => this.handleServerCallback(res, null))
            .catch(err => this.handleServerCallback(null, err));
    }

    //sub: _handleCoreSessionResponse
    private handleCoreSessionResponse(response: CoreResponseModel, coreSession: AbstractJavaSession) : void {
        //se estado não estiver apontando que estamos no meio de um carregamento de certificados, desconsidera o que recebemos
        if (this.state !== CertificateCacheState.LoadingCertificates)
            return console.error("Recebida lista de certificados do java, porém ela foi DESCARTADA, devido à que o estado do cache de certificados está com valor " + this.state);

        if (!this.cachedResponse)
            this.cachedResponse = response;
        else
            this.cachedResponse.result.certs = this.cachedResponse.result.certs.concat(response.result.certs);

        if (response.result.totalCerts > this.cachedResponse.result.certs.length) {
            var reloadStore = true;
            var includeChain = this.is_certProcessingEnabled();

            coreSession.listCertificates(this.coreSessionResponseHandler, includeChain, reloadStore, this.cachedResponse.result.certs.length);

            return;
        }

        this.state = CertificateCacheState.CertificatesLoaded;

        //dispara evento que carregamos a lista
        this.fireCertListLoadedEvent(this.cachedResponse);

        //se termos carregado os dados do certificado já conta como "Ready", disparamos tal evento
        if (this.is_certListReady()) this.fireCertListReadyEvent(this.cachedResponse);
    }

    //sub: _registerCertListProcessingOnLoadedCertList
    private registerCertListProcessingOnLoadedCertList() : void {
        //registra método de processamento de lista no momento do carregamento da lista de certificados
        if (!this.certListProcessingScheduled) {
            this.add_certListLoaded(this.processLoadedCertListHandler);
            this.certListProcessingScheduled = true;
        }
    }

    private unregisterCertListProcessingOnLoadedCertList() : void {
        //desregistra método de processamento de lista no momento do carregamento da lista de certificados
        if (this.certListProcessingScheduled) {
            this.remove_certListLoaded(this.processLoadedCertListHandler);
            this.certListProcessingScheduled = false;
        }
    }

    /**
     * Reseta estado do carregamento de certificados no cache, e também limpa as respostas em cache
     */
    private discardCacheState() : void {
        //limpa o estado atual do cache e vai para carregando certificados
        this.state = CertificateCacheState.Initial;
        this.cachedResponse = null;
        this.processed = false;
    }

    private reloadCacheInternal(coreSession: AbstractJavaSession) : void {
        //limpa o estado do cache
        this.discardCacheState();

        this.state = CertificateCacheState.LoadingCertificates;
        //chama método do de listagem de certificados na sessão do core
        const reloadStore: boolean = true;
        const includeChain: boolean = this.is_certProcessingEnabled();
        coreSession.listCertificates(this.coreSessionResponseHandler, includeChain, reloadStore);
    }

    /** Limpa lista de certificados e consulta novamente
     * @param coreSession  Sessão java utilizada para consulta
     */
    reloadCache(coreSession: AbstractJavaSession) : void {
        //se tiver no meio de um reload, cai fora
        if (this.state === CertificateCacheState.LoadingCertificates) return;

        //desregistra evento de processamento de certificados (se tiver registrado algum)
        this.unregisterCertListProcessingOnLoadedCertList();
        //chama método interno de reload
        this.reloadCacheInternal(coreSession);
    }

    /** Busca certificados na store
     * @param coreSession     Sessão java a ser utilizada
     * @param callbackMethod  Callback para processamento do retorno 
     */
    listCertificates(coreSession: AbstractJavaSession, callbackMethod: (CoreResponseModel) => void) : void {
        //[Estado 'ready'] Se a lista de certificados está pronta, chama método de callback (assincronamente) e cai fora
        if (this.is_certListReady()) {
            setTimeout(callbackMethod, 0, this.cachedResponse);
            return;
        } 

        //adiciona método de callback na lista de handlers de quando tivermos a lista de certificados pronta
        this.add_certListReady(callbackMethod);

        //[Estado '1'] Se estamos em um estado inicial, precisamos iniciar o carregamento do cache de certificados.
        //chamar '_reloadCacheInternal' sempre muda para o estado 2 !!!
        if (this.state === CertificateCacheState.Initial) {
            this.reloadCacheInternal(coreSession);
        }

        //[Estado '2'] Se processamento server-side estiver habilitado, precisamos registrar o método de processamento
        //de certificados quando o carregamento de certificados finalizar
        if (this.state === CertificateCacheState.LoadingCertificates && this.is_certProcessingEnabled()) {
            return this.registerCertListProcessingOnLoadedCertList();
        }

        //[Estado '3'] Temos uma lista de certificados carregada. O fato dela não ter sido qualificada como 'ready' indica que temos
        //processamento server-side. Como a lista de certificados já está carregada, vamos processá-la imediatamente.
        //Invocar "_processCertList" sempre muda o estado para 4 !!!
        if (this.state === CertificateCacheState.CertificatesLoaded) {
            this.processCertList(this.cachedResponse);
        }
    }
}

/**
 * Interface que simboliza correlação entre ida e volta de callbacks
 */
class CallbackCorrelationInfo {
    sessionId: string;
    signerDataRevision: number;
    isErrorReport: boolean;
    constructor(
        sessionId: string,
        signerDataRevision: number,
        isErrorReport: boolean) 
    {
        this.sessionId = sessionId;
        this.signerDataRevision = signerDataRevision;
        this.isErrorReport = isErrorReport;
    }

    matches(another: CallbackCorrelationInfo): boolean {
        //prioridade é o sessionId
        if (this.sessionId) return this.sessionId === another.sessionId;
        //vamos por revisão mesmo
        return this.signerDataRevision && this.signerDataRevision === another.signerDataRevision;
    }
}

/** Classe que contém dados de uma sessão de assinatura
 */
class SignerSessionData {
    constructor() {
        this.progress = { total: 0, done: 0 };
    }
    sessionId: string;
    signatureAlgorithm: string;
    certId: string;
    progress: {
        total: number;
        done: number;
    };

    /**
     * Propriedade que indica se um cancelamento foi solicitado para a sessão atual
     */
    cancelled: boolean;
    /**
     * Código numérico que identifica o processo de assinatura no client-side (revisão da sessiondata)
     */
    revision: number;

    /**
     * Cria e retorna uma info de correlação para callbacks
     */
    makeCallbackCorrelationInfo() : CallbackCorrelationInfo {
        return new CallbackCorrelationInfo(this.sessionId, this.revision, false);
    }

    /**
     * Retorna se os dados atuais desta sessão de assinatura correspondem com uma info de correção de callback
     * @param correlationInfo Informação de correção de callback
     */
    matchesCallbackCorrelationInfo(correlationInfo: CallbackCorrelationInfo) : boolean {
        //prioridade é o sessionId
        if (this.sessionId) return this.sessionId === correlationInfo.sessionId;
        //vamos por revisão mesmo
        return this.revision && this.revision === correlationInfo.signerDataRevision;
    }
};

/**
 * Tipo que representa o callback de sucesso e falha
 */
type RestCallbackHandlerFunc = (result: SessionStateResult | StartSessionStateResult, correlationInfo: CallbackCorrelationInfo) => void;

/**
 * Classe que é responsável por controlar a fila de requests
 */
class SignerSessionBatchReporter
{
    private queue: Array<[BatchProcessingArguments, CallbackCorrelationInfo]> = [];
    
    private requestPending: boolean = false;

    constructor(
        private http: SignerSessionHttpServices,
        private onReportSuccess: RestCallbackHandlerFunc,
        private onReportFailure: RestCallbackHandlerFunc
    ) { }

    /**
     * Adiciona uma request de batch na fila
     * @param batchInfo argumentos do batch
     * @param correlationInfo informações da sessão
     */
    enqueue(batchInfo: BatchProcessingArguments, correlationInfo: CallbackCorrelationInfo) {
        this.queue.push([batchInfo, correlationInfo]);
        this.startSending();
    }

    private startSending() {
        //verifica se já não tem ninguém executando ou se fila está vazia
        if (this.requestPending || this.queue.length < 1) return;

        this.requestPending = true;

        //retira o primeiro item da lista e dispara a request
        const [batchArgs, correlation] = this.queue.shift();
        this.fireRequest(batchArgs, correlation);
    }

    private fireRequest(batchInfo: BatchProcessingArguments, correlationInfo: CallbackCorrelationInfo) {
        //envia callback para o servidor
        this.http.sendBatch(batchInfo)
            .then(res => this.handleRequestSuccess(res, correlationInfo))
            .catch(err => this.handleRequestFailure(err, correlationInfo));
    }

    private handleRequestSuccess(res: SessionStateResult, correlationInfo: CallbackCorrelationInfo) {
        //dispara o handler onreportsuccess
        try {
            this.onReportSuccess(res, correlationInfo);
        } catch (e) {
            console.error(e);
        }
        
        //limpa indicador de request pendente e reschedula próxima execução
        this.requestPending = false;
        setTimeout(() => this.startSending(), 0);
    }

    private handleRequestFailure(err: any, correlationInfo: CallbackCorrelationInfo) {
        //limpa os itens da sessão na fila
        this.clearQueue(correlationInfo);

        //dispara o handler onreportfailure
        try {
            this.onReportFailure(err, correlationInfo);
        } catch (e) {
            console.error(e);
        }

        //limpa indicador de request pendente e reschedula próxima execução
        this.requestPending = false;
        setTimeout(() => this.startSending(), 0);
    }

    private clearQueue(correlationInfo: CallbackCorrelationInfo) {
        //remove de "queue" todas as requests que forem do mesmo correlation
        //(filtra deixando apenas as de correlation diferente)
        this.queue = this.queue.filter(([, corr]) => !corr.matches(correlationInfo));
    }
}

/** Classe que representa uma sessão de assinatura
 */
export class SignerSession
{
    private events: EventEmitter = new EventEmitter();

    private state: SignerSessionState = SignerSessionState.NotStarted;

    private sessionData: SignerSessionData = new SignerSessionData();
    
    private coreSession: AbstractJavaSession = null;

    private signRequestId: string = null;

    private hasCertificateListPreProcess: boolean = false;

    private lastError: ErrorInfo = null;

    private inputsQueue: SignatureInput[] = null;

    private javaSignCallbackHandler: (CoreResponseModel) => void = null;

    private onCoreSessionCreatedHandler: (CoreResponseModel) => void = null;

    private certCache: CertificateCache = null;

    private signerSessionDataRevisionSeed:number = 0;

    private batchReporter: SignerSessionBatchReporter;

    /** Construtor
     * @param certProcessingEnabled  Define se pré-processamento de lista de certificados estará habilitado
     * @param http                   Objeto responsável pelas comunicações com webservice de assinatura digital
     */
    constructor(
        private certProcessingEnabled: boolean,
        private http: SignerSessionHttpServices
    ) {
        //cria reporter de lotes (queue) para enviar assinaturas para o server de forma serializada
        this.batchReporter = new SignerSessionBatchReporter(
            this.http,
            this.handleCallbackResult.bind(this),
            this.handleCallbackError.bind(this)
        );
    }

    private getSignerCore() : SignerCore {
        const core = SignerCore.getInstance();
        if (!core) throw new Error("Signer core não está carregado");

        return core;
    }

    get_hasCertificateListPreProcess() : boolean { return this.hasCertificateListPreProcess; }
    set_hasCertificateListPreProcess(hasCertificateListPreProcess: boolean) : void { this.hasCertificateListPreProcess = hasCertificateListPreProcess; }

    /** Adiciona handler para quando ocorrer erro no processo de assinatura
     * @param handler  Processo a ser executado
     */
    add_processErrorOccurred(handler: (any) => void) : void { this.events.on("processErrorOccurred", handler); }
    /** Remove handler para quando ocorrer erro no processo de assinatura
     * @param handler  Processo a ser removido
     */
    remove_processErrorOccurred(handler: (any) => void) : void { this.events.removeListener("processErrorOccurred", handler); }

    /** Adiciona handler para alteração no progresso de assinaturas do lote 
     * @param handler  Processo a ser executado
     */
    add_progressChanged(handler: (any) => void) : void { this.events.on("progressChanged", handler); }
    /** Remove handler para alteração no progresso de assinaturas do lote 
     * @param handler  Processo a ser removido
     */
    remove_progressChanged(handler: (any) => void) : void { this.events.removeListener("progressChanged", handler); }

    /** Adiciona handler para finalização do processo de assinatura 
     * @param handler  Processo a ser executado
     */
    add_signProcessFinished(handler: (any) => void) : void { this.events.on("signProcessFinished", handler); }
    /** Remove handler para finalização do processo de assinatura 
     * @param handler  Processo a ser removido
     */
    remove_signProcessFinished(handler: (any) => void) : void { this.events.removeListener("signProcessFinished", handler); }

    /** Adiciona handler para recepção de dados customizados
     * @param handler  Processo a ser executado
     */
    add_userCallbackDataReceived(handler: (arg: string[]) => void) : void { this.events.on("userCallbackDataReceived", handler); }
    /** Remove handler para recepção de dados customizados
     * @param handler  Processo a ser removido
     */
    remove_userCallbackDataReceived(handler: (arg: string[]) => void) : void { this.events.removeListener("userCallbackDataReceived", handler); }

    /** Adiciona handler para sessão adquirida
     * @param handler  Processo a ser executado
     */
    private add_coreSessionAcquired(handler: (AbstractJavaSession) => void) : void { this.events.on("coreSessionAcquired", handler); }
    
    private fireCoreSessionAcquired () {
        this.events.emit("coreSessionAcquired");
        this.events.removeAllListeners("coreSessionAcquired");
    }

    /** Busca estado da sessão de assinatura
     * @returns Estado da sessão de assinatura
     */
    get_state() : SignerSessionState { return this.state; }

    /** Busca último erro ocorrido
     * @param Último erro ocorrido
     */
    get_lastError() : ErrorInfo { return this.lastError; }

    /** Verifica se existe uma sessão ativa
     * @returns Verdadeiro se existe uma sessão ativa, senão falso
     */
    has_activeSession() : boolean {
        return !!this.coreSession;
    }

    /** Verifica se está processando assinaturas
     * @returns Verdadeiro se está processando assinaturas, senão falso
     */
    is_processingSignatures() : boolean {
        return this.state >= SignerSessionState.StartingSignature && this.state <= SignerSessionState.Finishing;
    }

    /** Verifica se sessão está no estado finalizado
     */
    is_finished() : boolean {
        return this.state === SignerSessionState.Finished;
    }

    /**
     * Retorna se a sessão atual está marcada como cancelada
     */
    is_currentSessionCancelled(): boolean {
        return this.sessionData.cancelled;
    }

    private disposeCoreSession() : void {
        if (this.coreSession) {
            //tenta pegar core, fazendo dispose da sessão (este código não chama "_getSignerCore", pois pode acontecer do signercore
            //já ter levado um dispose(), o que levaria todas as sessões juntas
            const signerCore: SignerCore = SignerCore.getInstance();
            if (signerCore) signerCore.destroySession(this.coreSession);

            //nula referência da sessão
            this.coreSession = null;
        }
    }

    private ensureCoreSession() : AbstractJavaSession {
        //pega core session e garante que esteja criado
        const coreSession = this.coreSession;
        if (!coreSession) throw new Error("Nenhuma sessão com componente Core do assinador está estabelecida");
        //retorna sessão core obtida
        return coreSession;
    }

    private isSigningInJava() : boolean {
        return !!this.signRequestId;
    }

    private cancelJavaSignatureUnchecked() : void {
        //se tiver pacote sendo assinado em java, cancela ele
        if (this.isSigningInJava()) {
            this.ensureCoreSession().cancelRequest(this.signRequestId, null);
            this.signRequestId = null;
        }
    }

    private clearSessionInfo() : void {
        const sd = this.sessionData;
        sd.sessionId = null;
        sd.signatureAlgorithm = null;
        sd.certId = null;
        sd.progress.total = 0;
        sd.progress.done = 0;
        sd.cancelled = false;
        sd.revision = 0;
    }

    private raiseEventSafely(eventName: string, eventArgs: any) : boolean {
        //invoca evento, de forma segura (com try/catch)
        try {
            this.events.emit(eventName, eventArgs);
        }
        catch (ex) {
            console.error("[signer session] Erro detectado ao disparar evento '" + eventName + "'");
            console.dir(ex);
            return false;
        }
        //tudo OK
        return true;
    }

    private raiseProcessError(error: ErrorInfo) : boolean {
        return this.raiseEventSafely("processErrorOccurred", error);
    }

    private raiseFinishedEvent() : boolean {
        return this.raiseEventSafely("signProcessFinished", this.lastError);
    }

    private raiseProgressChangedEvent() : boolean {
        return this.raiseEventSafely("progressChanged", this.sessionData.progress);
    }

    private raiseUserCallbackDataReceivedEvent(userCallbackDataList: string[]) : boolean {
        //percorre lista de dados customizados recebidos
        for (let i = 0, _i = userCallbackDataList.length; i < _i; ++i) {
            //invoca evento com try/catch por fora
            try {
                this.events.emit("userCallbackDataReceived", userCallbackDataList[i]);
            }
            catch (ex) {
                console.error("[signer session] Erro detectado ao disparar evento 'userCallbackDataReceived'");
                console.dir(ex);
                return false;
            }
        }

        //nenhuma exception enquanto era tentando disparar eventos
        return true;
    }

    private enterFinishedState() : void {
        //seta estado e dispara evento de finalizado
        this.state = SignerSessionState.Finished;
        this.raiseFinishedEvent();

        //limpa informações da sessão de assinatura e sessão core
        this.clearSessionInfo();
        this.disposeCoreSession();
    }
    
    private enterErrorState(error: ErrorInfo | string) : void {
        //se error for um objeto que contenha o atributo 'msg', já é um objeto de erro certinho... caso contrário temos que
        //criar um objeto de erro com esse formato e utilizar o erro passado como se fosse a mensagem
        if (typeof error !== "object" || !error.msg) {
            error = { msg: <string>error, stacktrace: null, type: null };
        }
        //salva erro como último
        this.lastError = error;
        //se está assinando alguma coisa no java, cancela tal operação
        this.cancelJavaSignatureUnchecked();
        //dispara evento de erro
        this.raiseProcessError(error);
        //vai para estado de finalizado
        this.enterFinishedState();
    }

    private enterReportingErrorState(error: ErrorInfo) : void {
        this.state = SignerSessionState.ReportingError;
        //cancela assinatura no java, se tiver alguma executando
        this.cancelJavaSignatureUnchecked();
        //envia callback para o servidor, com a mensagem de erro
        this.sendBatchCallbackForError(error);
    }

    private feedInputsQueue(newInputs: SignatureInput[]) : void {
        //copia elementos para fila de input
        if (newInputs && newInputs.length > 0) {
            Array.prototype.push.apply(this.inputsQueue, newInputs);
        }
    }

    private fetchInputs() : SignatureInput[] {
        //faz fetch de todos mesmo
        return this.inputsQueue.splice(0, Number.MAX_VALUE);
    }

    private sendBatchCallbackForError(signatureError: ErrorInfo) : void
    {
        //argumentos de callback da assinatura
        const callbackArgs: BatchProcessingArguments = {
            sessionId      : this.sessionData.sessionId,
            signatureError : signatureError,
            outputs        : null,
            statistics     : null
        };

        //cria correção para callback e marca como report de erros
        const correlationInfo:CallbackCorrelationInfo = this.sessionData.makeCallbackCorrelationInfo();
        correlationInfo.isErrorReport = true;

        //chamar fila de batches
        this.batchReporter.enqueue(callbackArgs, correlationInfo);
    }

    private sendBatchCallback(signatureOutputs: BatchProcessingOutput[], statistics?: Statistics) : void {
        //guarda legacy no método
        if (!signatureOutputs) return;

        //argumentos de callback da assinatura
        const callbackArgs: BatchProcessingArguments = {
            sessionId      : this.sessionData.sessionId,
            outputs        : signatureOutputs,
            signatureError : null,
            statistics     : statistics || null
        };

        //cria correção para callback
        const correlationInfo:CallbackCorrelationInfo = this.sessionData.makeCallbackCorrelationInfo();

        //chamar fila de batches
        this.batchReporter.enqueue(callbackArgs, correlationInfo);
    }

    private javaSignCallback(result: CoreResponseModel) : void {
        //se estado deste controle não estiver indicando que está assinando algo no java, ignora callback
        if (!this.isSigningInJava()) {
            console.warn("[signer session] Recebido callback de assinatura apesar do componente não estar assinando nada em java");
            return console.dir(result);
        }

        //se ocorreram erros na assinatura no java, vamos cancelar a sessão no servidor
        if (result.error) {
            return this.enterReportingErrorState(result.error);
        }
        else {
            //pega objeto de resultado da assinatura e verifica se vieram outputs assinados
            const signResult = result.result;
            if (signResult.outputs && signResult.outputs.length > 0) {
                this.sendBatchCallback(signResult.outputs, result.statistics);
            }
        }

        //se não tem próxima resposta, terminou de se comunicar com o java
        if (!result.hasNext) {
            //se ainda temos inputs na fila, invoca o java novamente, caso contrário terminamos a assinatura no java
            if (this.inputsQueue.length > 0) {
                this.invokeJavaSignature();
            }
            else { this.signRequestId = null; }
        }
    }

    private invokeJavaSignature() : void {
        //pega sessão core e lote de informações a serem assinadas
        const coreSession = this.ensureCoreSession();
        const batch = this.fetchInputs();

        //pega ID do certificado e algoritmo de assinatura necessários para o processo
        const certId = this.sessionData.certId;
        const algorithm = this.sessionData.signatureAlgorithm;

        //cria request de assinatura do lote
        this.signRequestId = coreSession.sign(certId, algorithm, batch, this.javaSignCallbackHandler);
    }

    private handleUserCallbackData(resultObj: SessionStateResult | StartSessionStateResult) : void {
        //verifica se vieram dados de callback customizados do usuário
        if (resultObj.userCallbackData && resultObj.userCallbackData.length > 0) {
            this.raiseUserCallbackDataReceivedEvent(resultObj.userCallbackData);
        }
    }

    private handleProcessStartCallbackResult(resultObj: StartSessionStateResult) : void {
        //valida se veio inputs (obrigatório inicialmente)
        if (!resultObj.inputsBatch || resultObj.inputsBatch.length < 1) {
            console.error("Resposta inicial não possui objetos de input");
            return this.enterErrorState("Erro no processo de assinatura, tente novamente");
        }

        //seta estágio deste objeto como 'started'
        this.state = SignerSessionState.SignatureStarted;

        //pega dados da nova sessão
        this.sessionData.sessionId = resultObj.sessionId;
        this.sessionData.progress.total = resultObj.totalInputs;
        this.sessionData.progress.done  = 0;
        this.sessionData.signatureAlgorithm = resultObj.algorithm;

        //dispara evento de mudaça do progresso (temos de verificar se o usuário não cancelou o processo neste evento de report)
        this.raiseProgressChangedEvent();
        if (this.is_currentSessionCancelled()) return;

        //reseta fila de inputs e usa o primeiro lote como sendo o inicial para assinatura
        this.inputsQueue = [];
        this.feedInputsQueue(resultObj.inputsBatch);

        //invoca o java para assinar inputs
        this.invokeJavaSignature();
    }

    private handleBatchProcessingCallbackResult(resultObj: SessionStateResult) : void {
        //atualiza dados e emite evento de mudança
        this.sessionData.progress.done = resultObj.processedOutputs;
        this.raiseProgressChangedEvent();

        //se finalizou o processo entre no estado de finalizado
        if (resultObj.finished) {
            return this.enterFinishedState();
        }

        //se usuário cancelou o processo no evento "ProgressChangedEvent", vamos parar por aqui
        if (this.is_currentSessionCancelled()) return;

        //alimenta inputs com os novos retornados
        this.feedInputsQueue(resultObj.inputsBatch);

        //caso o java tiver sido mais rápido que nós, vamos invocá-lo novamente
        if (!this.isSigningInJava() && this.inputsQueue.length > 0) {
            this.invokeJavaSignature();
        }
    }

    private handleCallbackResult(result: SessionStateResult | StartSessionStateResult, correlationInfo: CallbackCorrelationInfo) : void
    {
        //se o callback for para uma session diferente da atual, vamos ignorar ele
        if (!this.sessionData.matchesCallbackCorrelationInfo(correlationInfo)) {
            console.warn("Recebida resposta de callback que não corresponde à sessão atual. Possível sessão dangling em server-side");
            return console.dir(result);
        }

        //se estivermos no meio de um report de erros, só vamos considerar respostas que tenham vindo de tais requests
        let currState:SignerSessionState = this.state;
        if (currState === SignerSessionState.ReportingError && !correlationInfo.isErrorReport) {
            console.warn("Recebida resposta de callback sem correlação com report de erros, enquanto no estado de report de erros");
            return console.dir(result);
        }
        
        //verifica se é um callback que caracteriza o fim do processo
        if (result.finished) {
            currState = SignerSessionState.Finishing;
            this.state = currState;
        }

        //trata dados customizados que vieram no callback
        this.handleUserCallbackData(result);

        //verifica se veio erro, entrando em estado de erro
        if (result.error) {
            console.error(result.error);
            return this.enterErrorState("Erro no processo de assinatura, tente novamente");
        }

        //verifica se é uma resposta de início de assinatura
        const looksLikeStartSessionResponse:boolean = !!(<StartSessionStateResult>result).algorithm;
        if (looksLikeStartSessionResponse) {
            //verifica se está no estágio correto
            if (currState !== SignerSessionState.StartingSignature) {
                console.error("Recebida resposta de início de processo de assinatura, porém estágio da sessão estava com valor: " + currState);
                return this.enterErrorState("Erro no processo de assinatura, tente novamente");
            }
            //faz tratamento da resposta
            this.handleProcessStartCallbackResult(<StartSessionStateResult>result);
        }
        else {
            //verifica se está no estágio correto
            if (currState !== SignerSessionState.SignatureStarted && currState !== SignerSessionState.Finishing) {
                console.error("Recebida resposta de lote de assinaturas, porém estágio da sessão estava com valor: " + currState);
                return this.enterErrorState("Erro no processo de assinatura, tente novamente");
            }
            //faz tratamento da resposta
            this.handleBatchProcessingCallbackResult(result);
        }
    }

    private handleCallbackError(error: ErrorInfo, correlationInfo: CallbackCorrelationInfo) : void
    {
        //se o callback for para uma session diferente da atual, vamos ignorar ele
        if (!this.sessionData.matchesCallbackCorrelationInfo(correlationInfo)) {
            console.warn("Recebido erro em callback que não corresponde à sessão atual. Possível sessão dangling em server-side");
            return console.dir(error);
        }

        //entra em estado de erro
        this.enterErrorState(error);
    }

    /** Cancela processo de assinatura que estiver sendo realizado
     */
    cancelSignature() : void
    {
        //se sessão já está finalizada ou cancelada, não há nada para fazer
        if (this.is_finished() || this.is_currentSessionCancelled()) return;

        //pega estado atual do processo do signer
        const currentState:SignerSessionState = this.state;
        
        //se ainda estiver no estado de criação da sessão do core (nunca iniciou uma sessão de assinatura mesmo no server)
        //não precisamos fazer nada de especial (podendo manter o core criado certinho)
        if (currentState === SignerSessionState.CreatingCoreSession || currentState === SignerSessionState.CoreSessionCreated) return;

        //marca flag de cancelamento nos dados atuais da sessão
        this.sessionData.cancelled = true;

        //cria modelo de erro simbolizando cancelamento
        const cancelError:ErrorInfo = { msg: "Operação cancelada manualmente", type: null };

        //tricky: neste estado, foi uma request de início de sessão para o servidor porém ela ainda não voltou.
        //vamos pular direto para o estado de "Finished", pois não adianta enviar nenhuma notificação para o server-side
        if (currentState === SignerSessionState.StartingSignature) {
            return this.enterErrorState(cancelError);
        }

        //verifica se está processando assinaturas no java, entrando em estado de report de erro (consequentemente cancelando assinatura)
        this.enterReportingErrorState(cancelError);
    }

    private rebuildCertificateCacheInternal() : void {
        //pega sessão core e invoca reload do cache de certificados
        const coreSession: AbstractJavaSession = this.ensureCoreSession();
        this.certCache.reloadCache(coreSession);
    }

    private scheduleRebuildCertificateCacheInternal() : void {
        this.add_coreSessionAcquired(this.rebuildCertificateCacheInternal.bind(this));
    }

    /** Recria cache de certificados
     */
    rebuildCertificateCache() : void {
        //se já começou uma sessão de assinatura, vamos ignorar o pedido de rebuild, de forma silenciosa
        if (this.state >= SignerSessionState.StartingSignature)
            return console.warn("Ignorado pedido de rebuild cache, pois estado está '%s'", this.state);

        //se está no meio da criação de uma sessão com o core, faz schedule da operação, em vez de executar imediatamente
        if (this.state === SignerSessionState.CreatingCoreSession) {
            this.scheduleRebuildCertificateCacheInternal();
        }
        else { this.rebuildCertificateCacheInternal(); }
    }

    private listCertificatesInternal(callbackMethod: (CoreResponseModel) => void) : void {
        //pega sessão core e invoca listagem de certificados do cache
        const coreSession: AbstractJavaSession = this.ensureCoreSession();
        this.certCache.listCertificates(coreSession, callbackMethod);
    }

    private scheduleListCertificatesInternal(callbackMethod: (CoreResponseModel) => void) : void {
        this.add_coreSessionAcquired(sender => sender.listCertificatesInternal(callbackMethod));
    }

    /** Realiza consulta de certificados
     * @param callbackMethod  Callback para processamento do retorno
     */
    listCertificates(callbackMethod: (CoreResponseModel) => void) : void {
        //se está no meio da criação de uma sessão com o core, faz schedule da operação, em vez de executar imediatamente
        if (this.state === SignerSessionState.CreatingCoreSession) {
            this.scheduleListCertificatesInternal(callbackMethod);
        }
        else { this.listCertificatesInternal(callbackMethod); }
    }

    /** Inicia processo de assinatura
     * @param certificate  Informações do certificado a ser utilizado
     * @returns Promisse com dados da sessão de assinatura se certificado possui cadeia completa, senão void
     */
    startSignProcess(certificate: CertificateInfo): any {
        //valida certificado informado
        if (!certificate) throw new Error(`Parâmetro "certificate" obrigatório não informado`);
        if (!certificate.certID) throw new Error("Objeto de certificado informado não possui atributo 'certID' válido");
        if (!certificate.keyStoreType) throw Error("Objeto de certificado informado não possui atributo 'keyStoreType' válido");

        //verifica se já não está no meio do processamento de assinaturas
        if (this.is_processingSignatures())
            throw new Error("Um processo de assinatura já está ocorrendo no momento");

        //pega sessão do core
        const coreSession: AbstractJavaSession = this.ensureCoreSession();

        //limpa último erro ocorrido e informações de sessão que existirem
        this.lastError = null;
        this.clearSessionInfo();
        //seta nova revisão para session data
        this.sessionData.revision = ++this.signerSessionDataRevisionSeed;

        //muda estágio para iniciando e seta ID do certificado sendo utilizado
        this.state = SignerSessionState.StartingSignature;
        this.sessionData.certId = certificate.certID;
        
        //argumentos para o callback de início de assinatura (tenta pegar cadeia de certificados já do objeto de certificado passado)
        const startCommandArgs: SignatureProcessStartArguments = {
            keyStoreType : certificate.keyStoreType,
            certChain    : certificate.certChain
        };

        //cria info de correção para callbacks à seguir
        const correlationInfo:CallbackCorrelationInfo = this.sessionData.makeCallbackCorrelationInfo();

        //se já tem cadeia de certificados informada, vamos executar o callback
        if (startCommandArgs.certChain) {
            return this.http.startSession(startCommandArgs)
                .then(res => this.handleCallbackResult(res, correlationInfo))
                .catch(err => this.handleCallbackError(err, correlationInfo));
        }

        //não tendo a cadeia informada ainda, vamos pedir ela para o java
        const thisRef: SignerSession = this;
        coreSession.getCertChain(this.sessionData.certId, function (resp) {
            //se possui erro, vai para estado de erro
            if (resp.error) return thisRef.enterErrorState(resp.error);

            //seta cadeia resultante nos argumentos do callback e envia o callback para o servidor
            startCommandArgs.certChain = resp.result.chain;
            thisRef.http.startSession(startCommandArgs)
                .then(res => thisRef.handleCallbackResult(res, correlationInfo))
                .catch(err => thisRef.handleCallbackError(err, correlationInfo));
        });
    }

    private onCoreSessionCreated(coreSession: AbstractJavaSession) : void {
        //garante que o estado deste controle esteja esperando pela criação da core session
        if (this.state !== SignerSessionState.CreatingCoreSession)
            throw new Error("Recebido callback de criação de sessão Java durante o estágio " + this.state);

        //salva coreSession e muda o estágio deste controle
        this.coreSession = coreSession;
        this.state = SignerSessionState.CoreSessionCreated;

        //dispara evento de sessão do core obtida
        this.fireCoreSessionAcquired();
    }

    /** Cria nova sessão java não existe nenhuma sessão válida para utilização
     * @param callback  Callback para processar retorno
     */
    newJavaSession(callback: (AbstractJavaSession) => void) : void {
        //verifica se foi passado callback
        if (!callback) throw new Error(`Parâmetro "callback" obrigatório não informado`);
        if (typeof callback !== "function") throw new Error(`Tipo inválido do parâmetro "callback" (deve ser Function)`);

        //garante que o status está em um estado terminal ou que ainda não iniciamos nenhum processo de assinatura
        const needsSession: boolean = this.is_finished() || this.state === SignerSessionState.NotStarted;
        if (!needsSession)
            throw new Error("Não é possível criar nova sessão de assinatura enquanto este controle estiver no estado " + this.state);

        //salva callback do usuário a ser invocado quando o java estiver pronto, assim como muda o estado para criando sessão java
        this.state = SignerSessionState.CreatingCoreSession;
        this.add_coreSessionAcquired(callback);

        //pega core de assinatura e pede para criar nova sessão Java
        this.getSignerCore().newJavaSession(this.onCoreSessionCreatedHandler);
    }

    /** Incializa sessão de assinatura
     */
    initialize() : void {
        //cria delegates
        this.javaSignCallbackHandler = this.javaSignCallback.bind(this);
        this.onCoreSessionCreatedHandler = this.onCoreSessionCreated.bind(this);

        this.certCache = new CertificateCache(this.certProcessingEnabled, this.http);
    }

    /** Limpa referências do objeto
     */
    dispose() : void {
        //nula delegates
        this.javaSignCallbackHandler = null;
        this.onCoreSessionCreatedHandler = null;

        //se tiver processo de assinatura rolando, vamos fazer um cancelamento emergencial
        if (this.is_processingSignatures()) {
            this.cancelJavaSignatureUnchecked();
            this.sendBatchCallbackForError({ msg: "Cancelamento emergencial: Objeto javascript recebeu dispose", type: null });
        }

        //faz dispose da core session sendo utilizada (se tiver)
        this.disposeCoreSession();

        //nula outras coisas
        this.certCache = null;
    }
}



// WEBPACK FOOTER //
// ./lib/signer-session.ts